import { Box, useTheme } from "@mui/material";
import { isEmpty } from "lodash";
import { useEffect, useMemo } from "react";

import { Divider } from "@/Components/v2/atoms/Divider";
import RoundedBorderContainer from "@/Components/v2/atoms/RoundedBorderContainer/RoundedBorderContainer";
import { Stack } from "@/Components/v2/atoms/Stack";
import { Typography } from "@/Components/v2/atoms/Typography";
import Palette from "@/Utils/Colors/palette";
import { isAboveSampleSizeThreshold, isBelowSampleSizeThreshold } from "@/Utils/dataFilters";
import { Layer } from "@/Utils/hierarchy";

import { MROIChartType, MROIColumnSelector } from "../../../constants/types";
import { useMROIStore } from "../../../store/useMROIStore";
import {
  AggregatedData,
  ChannelTotalROIMap,
  ComparisonNodeData,
  SharedRevenueAggregatedData,
} from "../../../types";
import BubbleChart from "./BubbleChart/BubbleChart";
import ChannelEffectivenessViewToggle from "./ChannelEffectivenessViewToggle";
import ColumnChart from "./ColumnChart/ColumnChart";
import { useColumnChartLegends } from "./ColumnChart/hooks/useColumnChartLegends";

const SPEND_THRESHOLD = 1000;
const NEGLIGIBLE_REVENUE_THRESHOLD = 0.1;

const ChannelEffectiveness = ({
  data,
  comparisonNodeData,
  nodeLayer,
  hasSharedRevenue,
  hasRoiRevenue,
}: {
  data: ChannelTotalROIMap;
  comparisonNodeData: ComparisonNodeData[];
  nodeLayer: Layer;
  hasSharedRevenue: boolean;
  hasRoiRevenue: boolean;
}) => {
  const { setChartType } = useMROIStore();
  const chartData = useMROIStore((state) => state.chart);
  const funnelStage = useMROIStore((state) => state.filters.funnelStage);
  const theme = useTheme();

  const targetNodeUUID = useMROIStore((state) => state.filters.targetNodeUUID);
  const { getLegendItems, legendOptions } = useColumnChartLegends(
    chartData.channelEffectiveness.column.selector
  );
  const palette = new Palette(theme.palette.bubbleChart.bubbleColors);

  const sortFunction = useMemo(() => {
    if (chartData.channelEffectiveness.chartType === MROIChartType.COLUMN) {
      return (
        a: [string, AggregatedData | SharedRevenueAggregatedData],
        b: [string, AggregatedData | SharedRevenueAggregatedData]
      ) => {
        const getValueForSelector = (data: AggregatedData | SharedRevenueAggregatedData) => {
          const selector = chartData.channelEffectiveness.column.selector;

          switch (selector) {
            case MROIColumnSelector.ROI_PERCENTAGE:
              return "roi" in data ? data.roi.total : 0;
            case MROIColumnSelector.SPEND_VS_REVENUE_PERCENTAGE:
              if (legendOptions.showMarketingSpend) {
                return "spendPercentage" in data ? data.spendPercentage.total : 0;
              }
              return "revenuePercentage" in data ? data.revenuePercentage.total : 0;
            case MROIColumnSelector.SPEND_VS_REVENUE_AMOUNT:
              if (legendOptions.showMarketingSpend) {
                return "spend" in data ? data.spend.total : 0;
              }
              return "revenue" in data ? data.revenue.total : 0;
            case MROIColumnSelector.SHARE_OF_EFFECT_AMOUNT:
            case MROIColumnSelector.MARKETING_RETURN_AMOUNT:
              return "revenue" in data ? data.revenue.total : 0;
            case MROIColumnSelector.SHARE_OF_EFFECT_PERCENTAGE:
            case MROIColumnSelector.MARKETING_RETURN_PERCENTAGE:
              return "revenuePercentage" in data ? data.revenuePercentage.total : 0;
            default:
              return 0;
          }
        };

        const aTotal = getValueForSelector(a[1]);
        const aSampleSize = a[1].sampleSize;

        const bTotal = getValueForSelector(b[1]);
        const bSampleSize = b[1].sampleSize;

        if (isBelowSampleSizeThreshold(aSampleSize)) return 1;
        if (isBelowSampleSizeThreshold(bSampleSize)) return -1;

        if (chartData.isAscending) {
          if (aTotal === 0 && bTotal !== 0) return 1;
          if (aTotal !== 0 && bTotal === 0) return -1;
          if (aTotal < 0 && bTotal > 0) return -1;
          if (aTotal > 0 && bTotal < 0) return 1;
          if (aTotal < 0 && bTotal < 0) return aTotal - bTotal;
          return aTotal - bTotal;
        } else {
          if (aTotal === 0 && bTotal !== 0) return 1;
          if (aTotal !== 0 && bTotal === 0) return -1;
          if (aTotal > 0 && bTotal < 0) return -1;
          if (aTotal < 0 && bTotal > 0) return 1;
          if (aTotal > 0 && bTotal > 0) return bTotal - aTotal;
          return bTotal - aTotal;
        }
      };
    }
    return undefined;
  }, [chartData, legendOptions]);

  const columnChartData = useMemo(() => {
    const sortedEntries = Object.entries(data)
      .filter((entry) => {
        if (entry[1].revenue.total < NEGLIGIBLE_REVENUE_THRESHOLD) return false;
        if (!chartData.showInsufficientData) {
          return isAboveSampleSizeThreshold(entry[1].sampleSize);
        }
        return true;
      })
      .sort(sortFunction);
    return {
      channels: sortedEntries.map(([channel]) => channel),
      nodes: sortedEntries.map(([_, node]) => node),
    };
  }, [data, chartData, sortFunction]);

  const bubbleChartData = useMemo(() => {
    const filteredEntries = Object.entries(data).filter((entry) => {
      return entry[1].spend.total > SPEND_THRESHOLD && entry[1].roas.total !== null;
    });
    return {
      channels: filteredEntries.map(([channel]) => channel),
      nodes: filteredEntries.map(([_, node]) => node),
    };
  }, [data, chartData]);

  useEffect(() => {
    if (funnelStage !== "all") setChartType(MROIChartType.COLUMN);
    if (funnelStage === "all" && isEmpty(bubbleChartData.nodes)) {
      setChartType(MROIChartType.COLUMN);
    }
  }, [funnelStage, bubbleChartData.nodes.length, setChartType]);

  const showChartToggle = funnelStage === "all";

  const getView = () => {
    if (chartData.channelEffectiveness.chartType === MROIChartType.BUBBLE) {
      return (
        <BubbleChart
          nodes={bubbleChartData.nodes}
          targetNodeUUID={targetNodeUUID ?? ""}
          comparisonNodeData={comparisonNodeData}
          nodeLayer={nodeLayer}
          palette={palette}
        />
      );
    }
    return (
      <ColumnChart
        nodes={columnChartData.nodes}
        hasSharedRevenue={hasSharedRevenue}
        hasRoiRevenue={hasRoiRevenue}
        legendItems={getLegendItems()}
        legendOptions={legendOptions}
      />
    );
  };

  return (
    <Box mx={4}>
      <RoundedBorderContainer>
        <Stack direction="row" justifyContent="space-between" mb={4} alignItems="center">
          <Typography variant="h2" fontSize={24} fontWeight={600}>
            Channel Effectiveness
          </Typography>
          {showChartToggle && <ChannelEffectivenessViewToggle />}
        </Stack>
        <Divider sx={{ mb: 2 }} />
        {getView()}
      </RoundedBorderContainer>
    </Box>
  );
};

export default ChannelEffectiveness;
